import { useCallback, useMemo } from "react";
import { useUser } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import { PoolMembership } from "../types/pools";
import { useApiAuth } from "./useApiAuth";
import { apiUrl } from "../lib/api";

type UsePoolsArgs = {
  enabled: boolean;
};

export type VirtualTraderDisclosure = {
  trader_code: string | null;
  display_name: string;
  participant_type: "VIRTUAL";
  profile: string;
  profile_description: string;
  status: string;
  pause_reason?: string | null;
};

export type VirtualTraderConfiguration = {
  enabled: boolean;
  requested_count: number;
  active_count: number;
  lifecycle_state: string;
  virtual_traders: VirtualTraderDisclosure[];
  updated_at?: string | null;
};

const parseApiError = async (response: Response, fallback: string) => {
  const errorText = await response.text();
  if (!errorText) return fallback;

  try {
    const parsed = JSON.parse(errorText) as { detail?: unknown; message?: unknown };
    if (typeof parsed.detail === "string") {
      return parsed.detail;
    }
    if (parsed.detail !== undefined) {
      return JSON.stringify(parsed.detail);
    }
    if (typeof parsed.message === "string") {
      return parsed.message;
    }
  } catch {
    // Keep raw response text when payload is not JSON.
  }

  return errorText;
};

export function usePools({ enabled }: UsePoolsArgs) {
  const { isSignedIn: isAuthenticated, user } = useUser();
  const { acquireToken } = useApiAuth();
  const canLoad = enabled && isAuthenticated;

  const query = useQuery({
    queryKey: ["pools", user?.id],
    enabled: canLoad && Boolean(user?.id),
    queryFn: async () => {
      const accessToken = await acquireToken();
      if (!accessToken) {
        throw new Error("Authentication required to load pools.");
      }

      const response = await fetch(apiUrl("/api/pools"), {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        const detail = await parseApiError(
          response,
          `Failed to load pools (${response.status})`,
        );
        throw new Error(`Failed to load pools (${response.status}): ${detail || "No error detail returned"}`);
      }

      const payload: PoolMembership[] = await response.json();
      return payload;
    },
  });

  const memberships = useMemo(() => (canLoad ? query.data ?? [] : []), [canLoad, query.data]);

  const configureVirtualTraders = useCallback(async (
    poolCode: string,
    enabled: boolean,
    count: number,
  ): Promise<{ success: boolean; data?: VirtualTraderConfiguration; error?: string }> => {
    const accessToken = await acquireToken();
    if (!accessToken) return { success: false, error: "Authentication required." };
    const response = await fetch(apiUrl(`/api/pools/${poolCode}/virtual-traders`), {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ enabled, count }),
    });
    if (!response.ok) {
      return { success: false, error: await parseApiError(response, "Could not update virtual traders.") };
    }
    return { success: true, data: (await response.json()) as VirtualTraderConfiguration };
  }, [acquireToken]);

  const getVirtualTraderConfiguration = useCallback(async (
    poolCode: string,
  ): Promise<{ success: boolean; data?: VirtualTraderConfiguration; error?: string }> => {
    const accessToken = await acquireToken();
    if (!accessToken) return { success: false, error: "Authentication required." };
    const response = await fetch(apiUrl(`/api/pools/${poolCode}/virtual-traders`), {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!response.ok) {
      return { success: false, error: await parseApiError(response, "Could not load virtual traders.") };
    }
    return { success: true, data: (await response.json()) as VirtualTraderConfiguration };
  }, [acquireToken]);

  return {
    memberships,
    isLoading: canLoad ? query.isLoading : false,
    isError: canLoad ? query.isError : false,
    reload: async () => {
      await query.refetch();
    },
    configureVirtualTraders,
    getVirtualTraderConfiguration,
  };
}

export type UsePoolsResult = ReturnType<typeof usePools>;
